import pluralize from 'pluralize';

import { type ClipType } from '@air/api/types';

import { getWorkspaceAgentGuidedVariantSkipKind } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentGuidedVariantSkipCounts';
import { buildWorkspaceAgentGuidedVariantSkipNonImagesClause } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePromptPhrases';
import {
  resolveWorkspaceAgentGuidedVariantPromptRenameSuffix,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  type WorkspaceAgentGuidedVariantSaveDestination,
  withWorkspaceAgentGuidedVariantSaveDestinationAndRename,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';

type WorkspaceAgentGrayscaleChannelWeights = {
  blue: number;
  green: number;
  red: number;
};

type WorkspaceAgentGrayscaleFilterDefinition = {
  id: string;
  label: string;
  previewColorMatrix: string;
  treatment: string;
  weights: WorkspaceAgentGrayscaleChannelWeights;
};

/**
 * Channel weights for `monochrome_image` on encoded sRGB bytes.
 * These match the recipes that used to be named presets on the tool.
 */
const WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS = {
  meadow: { blue: 0, green: 1, red: 0 },
  neutral: { blue: 0.0722, green: 0.7152, red: 0.2126 },
  silk: { blue: 0, green: 0.35, red: 0.65 },
  velvet: { blue: 0, green: 0.15, red: 0.85 },
} as const satisfies Record<string, WorkspaceAgentGrayscaleChannelWeights>;

const workspaceAgentGrayscalePreviewMatrix = ({ blue, green, red }: WorkspaceAgentGrayscaleChannelWeights): string => {
  const row = `${red} ${green} ${blue} 0 0`;

  return `${row} ${row} ${row} 0 0 0 1 0`;
};

export const WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID = {
  meadow: {
    id: 'meadow',
    label: 'Meadow',
    previewColorMatrix: workspaceAgentGrayscalePreviewMatrix(WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.meadow),
    treatment: 'green-channel grayscale',
    weights: WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.meadow,
  },
  neutral: {
    id: 'neutral',
    label: 'Neutral',
    previewColorMatrix: workspaceAgentGrayscalePreviewMatrix(WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.neutral),
    treatment: 'standard luminance grayscale',
    weights: WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.neutral,
  },
  silk: {
    id: 'silk',
    label: 'Silk',
    previewColorMatrix: workspaceAgentGrayscalePreviewMatrix(WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.silk),
    treatment: '0.65 red and 0.35 green grayscale',
    weights: WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.silk,
  },
  velvet: {
    id: 'velvet',
    label: 'Velvet',
    previewColorMatrix: workspaceAgentGrayscalePreviewMatrix(WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.velvet),
    treatment: '0.85 red and 0.15 green grayscale',
    weights: WORKSPACE_AGENT_GRAYSCALE_CHANNEL_WEIGHTS.velvet,
  },
} as const satisfies Record<string, WorkspaceAgentGrayscaleFilterDefinition>;

export type WorkspaceAgentGrayscaleFilterId = keyof typeof WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID;

export type WorkspaceAgentGrayscaleFilter =
  (typeof WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID)[WorkspaceAgentGrayscaleFilterId];

export const WORKSPACE_AGENT_GRAYSCALE_FILTERS = [
  WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID.neutral,
  WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID.velvet,
  WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID.silk,
  WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID.meadow,
] as const satisfies readonly WorkspaceAgentGrayscaleFilter[];

export type WorkspaceAgentGrayscaleSubject = {
  ext?: string | null;
  thumbnailUrl: string | null;
  type: ClipType;
};

export type WorkspaceAgentGrayscaleSelection = {
  imageCount: number;
  previewImageUrl: string | null;
  skippedFileCount: number;
  skippedVideoCount: number;
};

export const isWorkspaceAgentGrayscaleFilterId = (value: string): value is WorkspaceAgentGrayscaleFilterId =>
  Object.hasOwn(WORKSPACE_AGENT_GRAYSCALE_FILTERS_BY_ID, value);

export const getWorkspaceAgentGrayscaleSelection = (
  subjects: readonly WorkspaceAgentGrayscaleSubject[],
): WorkspaceAgentGrayscaleSelection => {
  let imageCount = 0;
  let previewImageUrl: string | null = null;
  let skippedFileCount = 0;
  let skippedVideoCount = 0;

  for (const subject of subjects) {
    const kind = getWorkspaceAgentGuidedVariantSkipKind(subject);

    if (kind === 'video') {
      skippedVideoCount += 1;
      continue;
    }

    if (kind === 'file') {
      skippedFileCount += 1;
      continue;
    }

    imageCount += 1;

    if (!previewImageUrl && subject.thumbnailUrl) {
      previewImageUrl = subject.thumbnailUrl;
    }
  }

  return { imageCount, previewImageUrl, skippedFileCount, skippedVideoCount };
};

export type BuildWorkspaceAgentGrayscalePromptInput = {
  destination: WorkspaceAgentGuidedVariantSaveDestination;
  filter: WorkspaceAgentGrayscaleFilter;
  imageCount: number;
  output?: WorkspaceAgentGuidedVariantOutput;
  skippedFileCount: number;
  skippedVideoCount: number;
};

export const buildWorkspaceAgentGrayscalePrompt = ({
  destination,
  filter,
  imageCount,
  output = WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  skippedFileCount,
  skippedVideoCount,
}: BuildWorkspaceAgentGrayscalePromptInput): string => {
  const target = imageCount === 1 ? 'the selected image' : 'each selected image';
  const { blue, green, red } = filter.weights;
  const skipNonImages = buildWorkspaceAgentGuidedVariantSkipNonImagesClause({
    featureAppliesToImagesPhrase: 'grayscale only applies to images',
    nonImageFilesWording: 'non-image files',
    skippedFileCount,
    skippedVideoCount,
  });

  return withWorkspaceAgentGuidedVariantSaveDestinationAndRename(
    `Apply the "${filter.label}" grayscale filter to ${target}: red ${red}, green ${green}, blue ${blue}.${skipNonImages}`,
    destination,
    resolveWorkspaceAgentGuidedVariantPromptRenameSuffix(filter.label, output),
  );
};

export type BuildWorkspaceAgentGrayscaleDescriptionInput = {
  filter: WorkspaceAgentGrayscaleFilter;
  skippedFileCount: number;
  skippedVideoCount: number;
};

export const buildWorkspaceAgentGrayscaleDescription = ({
  filter,
  skippedFileCount,
  skippedVideoCount,
}: BuildWorkspaceAgentGrayscaleDescriptionInput): string => {
  const summary = `${filter.label} grayscale — ${filter.treatment}`;
  const skippedCount = skippedFileCount + skippedVideoCount;

  if (skippedCount < 1) {
    return summary;
  }

  const noun = skippedFileCount > 0 ? 'file' : 'video';

  return `${summary}. ${skippedCount.toLocaleString()} ${pluralize(noun, skippedCount)} skipped.`;
};
